<template>
  <div class="container">
    <bundle-offer-modal
      :show.sync="openCreateModal"
      :organization-slug="organizationSlug"
      :bundle-offer="selectedOffer"
      @saved="onModalSaved"
      @error="onModalError"
    />
    <h1 class="subs-title">{{ adminText('organizationAll.bundle_offers_62c83b82', 'Bundle Offers') }}</h1>
    <h5 class="info-text">
      {{ adminText('organizationAll.easily_set_up_the_conditions_for_issuing_free_00ccad4e', 'Easily set up the conditions for issuing free tickets. Defined separately for each event by previously created ticket.') }}
    </h5>

    <div class="create-search-container">
      <main-button style="max-width: 244px; margin-bottom: 1rem" @click="onOpenCreateModal">
        <span class="add-symbol">+</span>{{ adminText('organizationAll.create_new_offer_50cd7965', 'Create new offer') }}
      </main-button>
      <v-text-field
        v-model="searchTerm"
        :label="adminText('organizationAll.search_bundles_by_events_06ba662f', 'Search bundles by Events')"
        append-icon="search"
        box
        style="max-width: 24.063rem"
      />
    </div>

    <v-data-table
      :items="bundleOffers"
      :headers="organizationHeaders(tableHeaders)"
      :pagination.sync="pagination"
      :total-items="totalBundleOffersCount"
      :loading="$apollo.loading"
      class="elevation-1 table-container"
      :rows-per-page-text="isDesktop ? adminText('organizationAll.rows_per_page_79df325c', 'Rows per page:') : ''"
      :no-data-text="adminText('organizationAll.no_bundle_offers_found_ff7b0831', 'No bundle offers found')"
    >
      <template v-slot:items="props">
        <tr style="cursor: pointer" @click="openEdit(props.item)">
          <td @click.stop.prevent>
            <color-toggle-button
              :label="findToggleLabel(props.item.status)"
              :type="findToggleType(props.item.status)"
              :value="findToggleValue(props.item.status)"
              hide-details
              @changeToggleValue="value => updateToggleSwitch(props.item.id, value)"
            />
          </td>
          <td>{{ props.item.eventName }}</td>
          <td>{{ props.item.ticketName }}</td>
          <td class="text-right">{{ props.item.quantityInterval }}</td>
          <td class="text-right">{{ props.item.freeQuantity }}</td>
          <td class="text-right">{{ bundleUsedQuantityText(props.item) }}</td>
          <td class="text-right">{{ props.item.startDate }}</td>
          <td class="text-right">{{ props.item.endDate }}</td>
        </tr>
      </template>
    </v-data-table>

    <simple-snackbar v-bind.sync="snackbar" :message-prop="organizationText(snackbar &amp;&amp; (snackbar.messageProp || snackbar.message))" />
  </div>
</template>

<script>
import OrganizationLocalization from '@/components/organizations/organization-all-display'

import { GET_BUNDLE_OFFERS_PAGINATED } from '@/graphql/bundleOffer.graphql'
import SimpleSnackbar from '@/components/shared/snackbars/simple-snackbar.vue'
import BundleOfferModal from '@/components/organizations/bundle-offers/BundleOfferModal.vue'
import moment from 'moment'
import { isNil, capitalize } from 'lodash'
import { ColorToggleTypes } from '@/types'
import ColorToggleButton from '@/components/shared/inputs/color-toggle-button.vue'
import { UPDATE_BUNDLE_OFFER_STATUS } from '@/graphql/bundleOffer.graphql'

const BundleStatus = Object.freeze({
  ACTIVE: 'AVAILABLE',
  STOPPED: 'STOPPED',
  CLOSED: 'CLOSED',
})

export default {
  mixins: [OrganizationLocalization],
  name: 'bundle-offers-container',
  components: {
    SimpleSnackbar,
    BundleOfferModal,
    ColorToggleButton,
  },
  props: {
    organizationSlug: {
      type: String,
    },
  },
  data() {
    return {
      bundleOffers: [],
      openCreateModal: false,
      selectedOffer: null,
      pagination: {
        page: 1,
        rowsPerPage: 10,
        sortBy: 'startDate',
      },
      snackbar: {},
      searchTerm: null,
      totalBundleOffersCount: 0,
      tableHeaders: [
        { text: 'Status', value: 'status', align: 'left', sortable: false },
        { text: 'Event name', value: 'eventName', align: 'left' },
        { text: 'Ticket name', value: 'ticketName', align: 'left' },
        { text: 'Quantity interval', value: 'quantityInterval', align: 'right' },
        { text: 'Free', value: 'freeQuantity', align: 'right' },
        { text: 'Used', value: 'quantityUsed', align: 'right' },
        { text: 'Start date', value: 'startDate', align: 'right' },
        { text: 'End date', value: 'endDate', align: 'right' },
      ],
      isDesktop: false,
    }
  },
  created() {
    window.addEventListener('resize', this.resizeHandler)
    if (window.innerWidth > 800) {
      this.isDesktop = true
    }
  },
  destroyed() {
    window.removeEventListener('resize', this.resizeHandler)
  },
  apollo: {
    getBundleOffersByOrganizationSlug: {
      query: GET_BUNDLE_OFFERS_PAGINATED,
      variables() {
        const pagination = {
          page: this.pagination.page,
          per_page: this.pagination.rowsPerPage,
          sortBy: this.pagination.sortBy,
          descending: this.pagination.descending,
        }

        return {
          organizationSlug: this.organizationSlug,
          pagination,
          searchTerm: this.searchTerm,
        }
      },
      result({ data, errors }) {
        if (errors) {
          this.snackbar = {
            open: true,
            messageProp: 'Error in fetching bundle offers',
            color: 'error',
          }

          return
        }
        this.bundleOffers = (data.getBundleOffersByOrganizationSlug.items || []).map(item => ({
          ...item,
          eventName: item.event.eventTemplateId
            ? `${item.event.title} (${moment(item.event.from).format('DD.MM.YYYY.')})`
            : item.event.title,
          ticketName: item.orderItem.name,
          startDate: moment(item.from).format('DD.MM.YYYY. HH:mm'),
          endDate: moment(item.to).format('DD.MM.YYYY. HH:mm'),
          status: this.getBundleOfferStatus(item),
          quantityUsedLabel: this.getBundleOfferUsedQuantity(item),
        }))
        this.totalBundleOffersCount = data.getBundleOffersByOrganizationSlug.totalCount
      },
    },
  },
  methods: {
    bundleUsedQuantityText(bundleOffer) {
      if (isNil(bundleOffer.totalQuantity)) return this.adminText('organizationAll.used_unlimited_ffd36ebb', '{{used}} / unlimited', { used: bundleOffer.quantityUsed })

      return this.getBundleOfferUsedQuantity(bundleOffer)
    },
    resizeHandler() {
      this.isDesktop = window.innerWidth > 800
    },
    async handleSearchChange(input) {
      this.searchTerm = input
      await this.$apollo.queries.getPromoCodes.refetch()
    },
    getBundleOfferStatus(bundleOffer) {
      if (!moment().isBetween(bundleOffer.from, bundleOffer.to)) {
        return BundleStatus.CLOSED
      } else if (bundleOffer.active) {
        return BundleStatus.ACTIVE
      } else {
        return BundleStatus.STOPPED
      }
    },
    getBundleOfferUsedQuantity(bundleOffer) {
      if (isNil(bundleOffer.totalQuantity)) {
        return `${bundleOffer.quantityUsed} / unlimited`
      } else {
        return `${bundleOffer.quantityUsed} / ${bundleOffer.totalQuantity}`
      }
    },
    openEdit(offer) {
      this.selectedOffer = offer
      this.openCreateModal = true
    },
    async onModalSaved() {
      this.snackbar = {
        open: true,
        messageProp: 'Bundle offer saved successfully',
        color: 'success',
      }
      this.selectedOffer = null
      await this.$apollo.queries.getBundleOffersByOrganizationSlug.refetch()
    },
    onModalError(error) {
      this.snackbar = {
        open: true,
        messageProp: error,
        color: 'error',
      }
    },
    onOpenCreateModal() {
      this.selectedOffer = null
      this.openCreateModal = !this.openCreateModal
    },
    findToggleLabel(status) {
      return this.organizationText(capitalize(status))
    },
    findToggleType(status) {
      if (status === BundleStatus.ACTIVE) {
        return ColorToggleTypes.SUCCESSFUL
      } else if (status === BundleStatus.STOPPED) {
        return ColorToggleTypes.FAILED
      } else {
        return ColorToggleTypes.DISABLED
      }
    },
    findToggleValue(status) {
      return status === BundleStatus.ACTIVE
    },
    async updateToggleSwitch(id, value) {
      const { errors, data } = await this.$apollo.mutate({
        mutation: UPDATE_BUNDLE_OFFER_STATUS,
        variables: {
          organizationSlug: this.organizationSlug,
          id,
          active: value,
        },
      })
      if (errors) {
        this.snackbar = {
          open: true,
          messageProp: errors[0].message,
          color: 'error',
        }
      }

      if (data && data.updateBundleOfferStatus) {
        this.bundleOffers = this.bundleOffers.map(offer => {
          if (offer.id === id) {
            offer.active = value
            offer.status = this.getBundleOfferStatus(offer)
          }

          return offer
        })
      }
    },
  },
}
</script>

<style scoped>
.info-text {
  font-family: 'Source Sans Pro';
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 150%;
  color: #979797;
  margin-bottom: 1rem;
}
.page-description {
  font-family: 'Source Sans Pro';
  font-style: normal;
  font-weight: 400;
  font-size: 1rem;
  color: #979797;
}
.container {
  padding: 0 !important;
  margin: 0 !important;
  max-width: 64.5rem;
  width: 100%;
}
.subs-title {
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 600;
  font-size: 24px;
  line-height: 120%;
  letter-spacing: -0.03em;
  font-feature-settings: 'pnum' on, 'lnum' on;
  color: #ff4c00;
  margin-bottom: 1.5rem;
}
.create-search-container {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  width: 100%;
}
@media screen and (max-width: 1411px) and (min-width: 800px) {
  .table-container {
    width: calc(100vw - 410px);
  }
}
</style>
